"use client";

import { useState } from "react";
import styled from "styled-components";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConnectionsStore } from "@/layout/connections/connectionsStore";
import { APIError, formatApiError } from '@/hooks/useApiError';
import { toast } from "sonner";
import {
  Loader2,
  Eye,
  EyeOff,
  Check,
  ArrowRight,
  ArrowLeft,
  ShoppingBag,
  AlertCircle,
} from "lucide-react";
import type { Marketplace, ValidateKeyResponse } from "@/types/api";

interface AddStoreModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const StepsIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Step = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  ${({ $active, $completed }) => {
    if ($completed) {
      return `
        background: var(--color-success);
        color: var(--color-text-inverse);
      `;
    }
    if ($active) {
      return `
        background: var(--color-accent-ring);
        color: var(--color-text-inverse);
      `;
    }
    return `
      background: var(--color-bg-skeleton);
      color: var(--color-text-tertiary);
    `;
  }}
`;

const MarketplaceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
`;

const MarketplaceCard = styled.button<{ $selected: boolean }>`
  padding: 24px;
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--color-accent-ring)' : 'var(--color-border-default)')};
  border-radius: 12px;
  background: ${({ $selected }) => ($selected ? 'var(--color-accent-light)' : 'var(--color-bg-surface)')};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  &:hover {
    border-color: var(--color-accent-ring);
  }
`;

const MarketplaceLogo = styled.div<{ $marketplace: Marketplace }>`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;

  ${({ $marketplace }) =>
    $marketplace === 'wildberries'
      ? `background: var(--color-wb); color: var(--color-text-inverse);`
      : `background: var(--color-ozon); color: var(--color-text-inverse);`}
`;

const MarketplaceName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-tertiary);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--color-text-secondary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HelpText = styled.p`
  font-size: 12px;
  color: var(--color-text-secondary);
  margin: 0;
`;

const HelpLink = styled.a`
  color: var(--color-accent);

  &:hover {
    text-decoration: underline;
  }
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin: 16px 0 8px 0;
  padding-top: 16px;
  border-top: 1px solid var(--color-border-default);
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: var(--color-error-light);
  border: 1px solid var(--color-error-border);
  border-radius: 8px;
  color: var(--color-error);
  font-size: 14px;
  margin-top: 16px;
`;

const SuccessInfo = styled.div`
  padding: 16px;
  background: var(--color-success-light);
  border: 1px solid var(--color-success-border);
  border-radius: 8px;
  margin-top: 16px;
`;

const StoreName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-success-text);
`;

const StoreId = styled.div`
  font-size: 14px;
  color: var(--color-success);
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

type WizardStep = "marketplace" | "credentials" | "confirm";

export function AddStoreModal({ open, onOpenChange }: AddStoreModalProps) {
  const [step, setStep] = useState<WizardStep>("marketplace");
  const [marketplace, setMarketplace] = useState<Marketplace | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [clientId, setClientId] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidateKeyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [performanceClientId, setPerformanceClientId] = useState("");
  const [performanceClientSecret, setPerformanceClientSecret] = useState("");
  const [showPerformanceSecret, setShowPerformanceSecret] = useState(false);

  const { addConnection, validateConnectionKey, setPendingSyncInfo } = useConnectionsStore();

  const isCredentialsValid = () => {
    if (!apiKey) return false;
    if (marketplace === "ozon") {
      return clientId && performanceClientId && performanceClientSecret;
    }
    return true;
  };

  const resetForm = () => {
    setStep("marketplace");
    setMarketplace(null);
    setApiKey("");
    setClientId("");
    setShowKey(false);
    setIsValidating(false);
    setIsSubmitting(false);
    setValidationResult(null);
    setError(null);
    setPerformanceClientId("");
    setPerformanceClientSecret("");
    setShowPerformanceSecret(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleMarketplaceSelect = (mp: Marketplace) => {
    setMarketplace(mp);
    setError(null);
  };

  const handleNextToCredentials = () => {
    if (marketplace) {
      setStep("credentials");
      setError(null);
    }
  };

  const handleValidate = async () => {
    if (!marketplace || !apiKey) return;

    if (marketplace === "ozon" && !clientId) {
      setError("Client ID обязателен для Ozon");
      return;
    }

    if (marketplace === "ozon" && (!performanceClientId || !performanceClientSecret)) {
      setError("Performance API credentials обязательны для Ozon");
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await validateConnectionKey({
        marketplace,
        api_key: apiKey,
        client_id: marketplace === "ozon" ? clientId : undefined,
      });

      if (result.valid) {
        setValidationResult(result);
        setStep("confirm");
      } else {
        setError(result.error || "Не удалось проверить ключ");
      }
    } catch (err) {
      const apiError = err as APIError;
      setError(formatApiError(apiError));
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (!marketplace || !apiKey) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const connection = await addConnection({
        marketplace,
        api_key: apiKey,
        client_id: marketplace === "ozon" ? clientId : undefined,
        performance_client_id:
          marketplace === "ozon" && performanceClientId.trim()
            ? performanceClientId.trim()
            : undefined,
        performance_client_secret:
          marketplace === "ozon" && performanceClientSecret.trim()
            ? performanceClientSecret.trim()
            : undefined,
      });
      toast.success(
        `${marketplace === "wildberries" ? "Wildberries" : "Ozon"} магазин добавлен`
      );
      if (connection.sync_task_id) {
        setPendingSyncInfo({ taskId: connection.sync_task_id, marketplace });
      }
      handleClose();
    } catch (err) {
      const apiError = err as APIError;
      setError(formatApiError(apiError));
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === "credentials") {
      setStep("marketplace");
      setApiKey("");
      setClientId("");
      setPerformanceClientId("");
      setPerformanceClientSecret("");
      setError(null);
    } else if (step === "confirm") {
      setStep("credentials");
      setValidationResult(null);
      setError(null);
    }
  };

  const getStepNumber = () => {
    switch (step) {
      case "marketplace": return 1;
      case "credentials": return 2;
      case "confirm": return 3;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case "marketplace": return "Выберите маркетплейс";
      case "credentials": return "Введите API-ключ";
      case "confirm": return "Подтверждение";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case "marketplace": return "С каким маркетплейсом вы работаете?";
      case "credentials": return "Введите данные для подключения к API";
      case "confirm": return "Проверьте данные и подтвердите добавление";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <StepsIndicator>
            {[1, 2, 3].map((num) => (
              <Step
                key={num}
                $active={getStepNumber() === num}
                $completed={getStepNumber() > num}
              >
                {getStepNumber() > num ? <Check size={16} /> : num}
              </Step>
            ))}
          </StepsIndicator>
          <DialogTitle>{getStepTitle()}</DialogTitle>
          <DialogDescription>{getStepDescription()}</DialogDescription>
        </DialogHeader>

        {step === "marketplace" && (
          <>
            <MarketplaceGrid>
              <MarketplaceCard
                $selected={marketplace === "wildberries"}
                onClick={() => handleMarketplaceSelect("wildberries")}
              >
                <MarketplaceLogo $marketplace="wildberries">WB</MarketplaceLogo>
                <MarketplaceName>Wildberries</MarketplaceName>
              </MarketplaceCard>

              <MarketplaceCard
                $selected={marketplace === "ozon"}
                onClick={() => handleMarketplaceSelect("ozon")}
              >
                <MarketplaceLogo $marketplace="ozon">OZON</MarketplaceLogo>
                <MarketplaceName>Ozon</MarketplaceName>
              </MarketplaceCard>
            </MarketplaceGrid>

            <ButtonGroup>
              <Button
                onClick={handleNextToCredentials}
                disabled={!marketplace}
                className="w-full"
              >
                Далее
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </ButtonGroup>
          </>
        )}

        {step === "credentials" && (
          <>
            <div className="space-y-4">
              {marketplace === "ozon" && (
                <FormGroup>
                  <Label htmlFor="clientId">Client ID *</Label>
                  <Input
                    id="clientId"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Например: 123456"
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label htmlFor="apiKey">API-ключ *</Label>
                <InputWrapper>
                  <Input
                    id="apiKey"
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={
                      marketplace === "wildberries"
                        ? "Вставьте API ключ из личного кабинета WB"
                        : "Вставьте API ключ из личного кабинета Ozon"
                    }
                    style={{ paddingRight: 40 }}
                  />
                  <ToggleButton type="button" onClick={() => setShowKey(!showKey)}>
                    {showKey ? <EyeOff /> : <Eye />}
                  </ToggleButton>
                </InputWrapper>
                <HelpText>
                  {marketplace === "wildberries" ? (
                    <>
                      Получить ключ можно в{" "}
                      <HelpLink
                        href="https://seller.wildberries.ru/supplier-settings/access-to-api"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Личном кабинете WB
                      </HelpLink>
                    </>
                  ) : (
                    <>
                      Получить ключ можно в{" "}
                      <HelpLink
                        href="https://seller.ozon.ru/app/settings/api-keys"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Личном кабинете Ozon
                      </HelpLink>
                    </>
                  )}
                </HelpText>
              </FormGroup>

              {marketplace === "ozon" && (
                <>
                  <SectionTitle>Performance API (реклама) *</SectionTitle>

                  <FormGroup>
                    <Label htmlFor="perfClientId">Performance Client ID *</Label>
                    <Input
                      id="perfClientId"
                      type="text"
                      placeholder="xxxx-xxxxxxxx@advertising.performance.ozon.ru"
                      value={performanceClientId}
                      onChange={(e) => setPerformanceClientId(e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="perfClientSecret">Performance Client Secret *</Label>
                    <InputWrapper>
                      <Input
                        id="perfClientSecret"
                        type={showPerformanceSecret ? "text" : "password"}
                        placeholder="Секретный ключ Performance API"
                        value={performanceClientSecret}
                        onChange={(e) => setPerformanceClientSecret(e.target.value)}
                        style={{ paddingRight: 40 }}
                      />
                      <ToggleButton
                        type="button"
                        onClick={() => setShowPerformanceSecret(!showPerformanceSecret)}
                      >
                        {showPerformanceSecret ? <EyeOff /> : <Eye />}
                      </ToggleButton>
                    </InputWrapper>
                    <HelpText>
                      Получить ключи можно в{" "}
                      <HelpLink
                        href="https://performance.ozon.ru/api"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ozon Performance
                      </HelpLink>
                    </HelpText>
                  </FormGroup>
                </>
              )}
            </div>

            {error && (
              <ErrorMessage>
                <AlertCircle size={16} />
                {error}
              </ErrorMessage>
            )}

            <ButtonGroup>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Назад
              </Button>

              <Button
                onClick={handleValidate}
                disabled={!isCredentialsValid() || isValidating}
                className="flex-1"
              >
                {isValidating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Проверка...
                  </>
                ) : (
                  "Проверить"
                )}
              </Button>
            </ButtonGroup>
          </>
        )}

        {step === "confirm" && validationResult && (
          <>
            <SuccessInfo>
              <StoreName>
                <ShoppingBag className="inline mr-2 h-5 w-5" />
                {validationResult.store_name || "Магазин"}
              </StoreName>
              {validationResult.store_id && (
                <StoreId>ID: {validationResult.store_id}</StoreId>
              )}
            </SuccessInfo>

            {error && (
              <ErrorMessage>
                <AlertCircle size={16} />
                {error}
              </ErrorMessage>
            )}

            <ButtonGroup>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Назад
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Добавление...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Подтвердить
                  </>
                )}
              </Button>
            </ButtonGroup>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
